import React, { useEffect } from 'react';
import styled from 'styled-components';
import KebabIcon from '../assets/ellipsis-vertical.svg?react';
import GraduationCapIcon from '../assets/graduation-cap.svg?react';
import InfoIcon from '../assets/info-outline.svg?react';
import HelpIcon from '../assets/question-circle-outlined.svg?react';
import SearchIcon from '../assets/search.svg?react';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { synchronizeHouseholdWithServer } from '../data/use_household.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { BackArrow, BackButtonWrapper, DownArrow, headerHeight, Pencil, RedDotIcon, Title } from '../util/shared.tsx';
import { UploadSongButton } from '../util/upload_song_button.tsx';
import './header.css';
import { ProfileAvatar } from './profile_drawer/profile_avatar.tsx';

interface Props {
  title: string | React.ReactElement;
  isUploadSongButtonVisible?: boolean;
  onBack?: (event: any) => void; //optional callback function for back button in header
  onKebabClick?: () => void;
  onSearchClick?: () => void;
  onHelpClick?: () => void;
  onInfoClick?: () => void;
  onEditClick?: () => void;
  onTutorialsClick?: () => void;
  suppressIcons?: boolean;
  suppressBorder?: boolean;
  showProfileAvatar?: boolean; // main tabs only; opens the profile drawer
}

export const Header = ({
  isUploadSongButtonVisible,
  title,
  onBack,
  onKebabClick,
  onSearchClick,
  onHelpClick,
  onInfoClick,
  onEditClick,
  onTutorialsClick,
  suppressIcons,
  suppressBorder,
  showProfileAvatar,
}: Props) => {
  const [isPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);

  useEffect(() => {
    void synchronizeHouseholdWithServer();
  }, []);

  const isSearchVisible = onSearchClick && !suppressIcons;
  const isLeftJustified = !onKebabClick;
  const hasProfileAvatar = showProfileAvatar && !onBack;

  const hasRightIcons = onKebabClick || isUploadSongButtonVisible || isSearchVisible || onTutorialsClick || onInfoClick;
  let maxTitleWidth = '100%';
  if (onBack && hasRightIcons) {
    maxTitleWidth = '75%';
  } else if (hasRightIcons) {
    maxTitleWidth = '90%';
  } else if (onBack) {
    maxTitleWidth = '85%';
  }

  const OuterClass = isLeftJustified ? LeftJustifiedHeader : HeaderWrapper;
  const TitleClass = typeof title === 'string' ? Title : ElementTitle;

  return (
    <OuterClass key="header" $suppressBorder={suppressBorder}>
      {onBack ? (
        <BackButtonWrapper>
          {isPlayerMaximized ? <DownArrow onClick={onBack} /> : <BackArrow onClick={onBack} />}
        </BackButtonWrapper>
      ) : hasProfileAvatar ? (
        <ProfileAvatar />
      ) : (
        !isLeftJustified && <LeftPlaceholder />
      )}
      <TitleWrapper $maxWidth={maxTitleWidth}>
        <TitleClass
          $leftMargin={onBack || onKebabClick ? '0' : hasProfileAvatar ? '10px' : '20px'}
          $textAlign={isLeftJustified ? 'left' : 'center'}
        >
          {title}
        </TitleClass>
        {onEditClick && <Pencil onClick={onEditClick} />}
        {isUploadSongButtonVisible && <UploadSongButton />}
      </TitleWrapper>
      <HeaderIcons>
        {onTutorialsClick && (
          <div style={{ position: 'relative', marginRight: '8px' }}>
            <RedDotIcon />
            <GraduationCapIcon className="tutorialsHeaderIcon" onClick={() => onTutorialsClick()} />
          </div>
        )}
        {onKebabClick && (
          <div style={{ marginRight: '12px' }}>
            <KebabIcon className="kebabIcon" onClick={() => onKebabClick()} />
          </div>
        )}
        {/* TODO(hewitt): use <Link> here instead? */}
        {isSearchVisible && <SearchIcon className="searchHeaderIcon" onClick={() => onSearchClick()} />}
        {onHelpClick && <HelpButton onClick={() => onHelpClick()} />}
        {onInfoClick && <InfoIcon className="infoHeaderIcon" onClick={() => onInfoClick()} />}
      </HeaderIcons>
    </OuterClass>
  );
};

const HeaderWrapper = styled.div<{ $suppressBorder?: boolean }>`
  display: flex;
  min-height: ${headerHeight};
  height: ${headerHeight};
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${(props) => (props.$suppressBorder ? 'transparent' : 'var(--color-hairline)')};
  white-space: normal;
  background-color: var(--color-background);
`;

const LeftJustifiedHeader = styled(HeaderWrapper)`
  justify-content: space-between;
  grid-template-columns: revert;
`;

// Never shrinks: a long title truncates or wraps (see TitleWrapper) instead of squeezing the icons
const HeaderIcons = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: end;
  flex: 1 0 auto;

  & > * {
    flex-shrink: 0;
  }
`;

const LeftPlaceholder = styled.div`
  flex-grow: 1;
  flex-basis: 0;
`;

// min-width: 0 lets the title shrink below its natural text width, so a long church name truncates with an
// ellipsis (org page) or a plain title wraps, rather than pushing the header icons off the right edge
const TitleWrapper = styled.div<{ $maxWidth?: string }>`
  display: flex;
  align-items: center;
  max-width: ${(props) => props.$maxWidth};
  min-width: 0;
  gap: 10px;

  & > ${Title} {
    min-width: 0;
  }

  & > :not(${Title}) {
    flex-shrink: 0; /* pencil & upload button keep their size */
  }
`;

// Element titles (e.g. the org page's church name with its switcher menu) handle their own truncation, so they
// skip Title's -webkit-line-clamp box: legacy WebKit (iOS Safari before 26) drops text-overflow ellipses from the
// descendants of a -webkit-line-clamp box, which left a long church name clipped mid-word instead of ending in "…"
const ElementTitle = styled(Title)`
  display: block;
  -webkit-line-clamp: none;
`;

const HelpButton = styled(HelpIcon)`
  width: 40px; /* this is necessary for iOS but has no effect in Chrome -> size is unchangeable on both */
  padding-right: 12px;
  cursor: pointer;
  fill: var(--color-text);
`;
